/**
 * §A · Identity Card — mechanically extracted from BrandBiblePage (W1).
 * Zero behavior/visual change from the inline block it replaces; the page
 * still owns all state, autosave, and data fetching and passes it down.
 *
 * P1 fold-in (2026-08-10): row-level verification pills for the primary
 * location, the bb_identity singleton, and the license/insurance credential
 * rows — plus a section-header "Verify all" affordance. License/insurance
 * are deliberately §A's (see ProofLibraryPanel.tsx's CredentialsCard header
 * comment: it filters those two kinds OUT because this section owns them),
 * so this file reads `credentials` straight off the store the same way
 * VoiceProfilePanel/CompliancePanel do — that table was never prop-drilled
 * here and adding a prop for it would just rename a store selector.
 *
 * ACF Task 1 (2026-08-11): the four previously UI-less bb_locations columns
 * that already exist in the DB + backend allow-list — service_radius_miles,
 * hours (JSONB day map), tracking_phone, gbp_url — get an operator-editable
 * "Primary location details" box. Section-local: it reads initial values off
 * `primaryLocation` and commits on blur via `savePrimaryLocation` only (the
 * page owns `draft`/`set`, which has no keys for these four). Inputs are the
 * repo's uncontrolled IdentityField primitive, keyed by the row id so they
 * re-seed when the primary-location row changes. Each field writes exactly
 * one column; hours is the sole merged write because the column is a single
 * JSONB blob (spread existing, override one day, omit empty days).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import { Globe, Info, Lightbulb, Loader2, Mail, Phone, RefreshCw, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AttentionRow } from '../NeedsAttentionQueue';
import LocationList from '../merge-ux/LocationList';
import {
  isValidUnverifiedDraft,
  requestModelFieldSuggestions,
  type AISuggestRequest,
  type ContextItem,
  type FetchFieldSuggestions,
  type TargetFieldMapping,
} from '@/services/brandBibleAiSuggest';
import { FEATURE_FLAGS } from '@/config/environment';
import { EnrichmentDraftsPanel, type DraftRow } from '@/components/brand-bible-wizard/EnrichmentDraftsPanel';
import { VerificationRow } from '@/components/brand-bible-wizard/primitives';
import type { useSingleton } from '@/components/brand-bible-wizard/steps/useSingleton';
import { useBrandBibleWizardStore, selectEntries } from '@/stores/brandBibleWizardStore';
import type { BbEntry, BbPayloadMap, BbTable, IdentityPayload } from '@/features/brand-bible-wizard/types';
// §A explicit-save conversion (bb-phase2-explicit-save-brief-20260810): flip
// the shared form primitives to the formPrimitives module (PR #1673) so this
// section no longer imports the page for its controls. `BrandBibleDraft` is a
// type-only page import — a type never creates the section→page runtime cycle
// this move exists to break.
import {
  FieldLabel,
  IdentityField,
  SectionHeader as BbSectionHeader,
  TextField,
  TextArea,
} from '../formPrimitives';
import { BRAND_BIBLE_SECTION_BY_ID } from '../BrandBibleSectionNavigation';
import {
  useRegisterBbSectionSave,
  useBbDraftCommit,
  type BbSectionSaveRegistration,
  type DirtyFormSaveResult,
} from '../save';
import {
  EMPTY_ESSENTIALS_BUFFER,
  bufferedDraft,
  bufferedOnCamera,
  bufferedSocials,
  changeCount as bufferChangeCount,
  coerceIdentityField,
  coerceLocationPatch,
  isDirty as bufferIsDirty,
  setDraftField,
  setIdentityField,
  setLocationHours,
  setLocationRadius,
  setLocationScalar,
  setOwnerField,
  setOwnerOnCamera,
  setServiceLocations,
  setSocial,
  type DraftScalarField,
  type EssentialsBuffer,
  type IdentityScalarField,
} from '../essentialsBuffer';
import type { BrandBibleDraft } from '../../../pages/brand-bible/draftModel';

/** One item this section can verify in bulk — table + row id. */
interface PendingVerifyItem {
  table: BbTable;
  id: string;
}

// Shared AI-suggest types + HTTP live in `@/services/brandBibleAiSuggest`.
// This section imports them; it does not redefine the contract.

/**
 * The human-audited inventory of EVERY editable control in §A (19), by stable
 * key. This is the coverage contract: the three suggestion buttons below must
 * partition exactly this set, and the coverage-matrix test asserts it — so a
 * control added to §A without an assignment fails the suite. Deliberately NOT
 * derived from the groups (that would be tautological); the groups are checked
 * AGAINST this list.
 */
export const SECTION_A_EDITABLE_FIELDS = [
  // Region A/B — core identity grid + Owner box
  'business_name',
  'dba',
  'founded_year',
  'team_size',
  'website',
  'owner_name',
  'owner_title',
  'on_camera_ok',
  // Region C/D — contact grid + service locations
  'phone',
  'email',
  'booking_url',
  'socials',
  'service_locations',
  // Region E/F — Brand overview + primary-location details. `address` is
  // NOT a suggest target (rest-of-§A ruling #16): LocationList owns the
  // address; the legacy draft.address fallback is read-only context.
  'description',
  'service_radius_miles',
  'tracking_phone',
  'gbp_url',
  'hours',
] as const;

/**
 * Controls deliberately EXCLUDED from suggestion coverage:
 *  - `credentials` (the licence/insurance rows) expose verify state only,
 *    not editable fields, and an AI-suggested licence number would fabricate
 *    a compliance fact. (The `trade` disabled placeholder was removed —
 *    rest-of-§A #19 — dead box deleted; real trades[]/services[] is an
 *    #1670 schema item.) Asserted excluded so making it editable later
 *    forces a coverage decision.
 */
export const SECTION_A_EXCLUDED_FIELDS = ['credentials'] as const;

/**
 * The three subsection-local suggestion buttons and the fields each covers.
 * Placement mirrors the UI regions so a button sits with the controls it
 * targets. Every key here must appear in SECTION_A_EDITABLE_FIELDS exactly once.
 */
export const SECTION_A_SUGGESTION_GROUPS = [
  {
    id: 'business-identity',
    label: 'Business identity',
    fields: [
      ['business_name', 'Legal name'],
      ['dba', 'DBA'],
      ['founded_year', 'Founded'],
      ['team_size', 'Team size'],
      ['website', 'Website'],
      ['owner_name', 'Owner name'],
      ['owner_title', 'Owner title'],
      ['on_camera_ok', 'On camera'],
    ],
  },
  {
    id: 'contact-reach',
    label: 'Contact & reach',
    fields: [
      ['phone', 'Phone'],
      ['email', 'Email'],
      ['booking_url', 'Booking URL'],
      ['socials', 'Socials'],
      ['service_locations', 'Service locations'],
    ],
  },
  {
    id: 'description-location',
    label: 'Description & location details',
    fields: [
      ['description', 'Brand overview'],
      ['service_radius_miles', 'Service radius'],
      ['tracking_phone', 'Tracking phone'],
      ['gbp_url', 'GBP URL'],
      ['hours', 'Hours'],
    ],
  },
] as const;

const CONTEXT_VALUE_MAX = 1500;
const EXISTING_VALUE_MAX = 400;
const EXISTING_MAX = 20;
const CONTEXT_MAX = 24;

const HOURS_DAY_ALIAS: Record<string, string> = {
  mon: 'mon',
  monday: 'mon',
  tue: 'tue',
  tues: 'tue',
  tuesday: 'tue',
  wed: 'wed',
  wednesday: 'wed',
  thu: 'thu',
  thur: 'thu',
  thurs: 'thu',
  thursday: 'thu',
  fri: 'fri',
  friday: 'fri',
  sat: 'sat',
  saturday: 'sat',
  sun: 'sun',
  sunday: 'sun',
};

function clip(value: string, max: number): string {
  const trimmed = value.trim();
  return trimmed.length <= max ? trimmed : trimmed.slice(0, max);
}

function boundedExisting(values: Array<string | null | undefined>): string[] {
  const out: string[] = [];
  for (const raw of values) {
    if (typeof raw !== 'string') continue;
    const next = clip(raw, EXISTING_VALUE_MAX);
    if (!next) continue;
    out.push(next);
    if (out.length >= EXISTING_MAX) break;
  }
  return out;
}

function formatSocials(map: Record<string, string | null> | undefined): string {
  if (!map) return '';
  return Object.entries(map)
    .filter(([, url]) => typeof url === 'string' && url.trim() !== '')
    .map(([platform, url]) => `${platform}: ${url!.trim()}`)
    .join('; ');
}

function formatHours(map: Record<string, string> | undefined): string {
  if (!map) return '';
  return Object.entries(map)
    .filter(([, hours]) => hours.trim() !== '')
    .map(([day, hours]) => `${day}: ${hours.trim()}`)
    .join('; ');
}

/** Split a multi-value suggestion on comma / newline / semicolon / pipe. */
// Exported for the focused parser tests; not a component (repo precedent: per-line disable).
// eslint-disable-next-line react-refresh/only-export-components
export function parseDelimitedValues(value: string): string[] {
  return value
    .split(/[\n,;|]+/)
    .map((part) => part.trim())
    .filter(Boolean);
}

// eslint-disable-next-line react-refresh/only-export-components
export function parseOnCameraOk(value: string): boolean | null {
  const normalized = value.trim().toLowerCase().replace(/[_ ]+/g, '-');
  if (['yes', 'true', '1', 'on-camera', 'oncamera'].includes(normalized)) return true;
  if (['no', 'false', '0', 'off-camera', 'offcamera', 'off'].includes(normalized)) return false;
  return null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function parseHoursMap(value: string): Record<string, string> | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const out: Record<string, string> = {};
  if (trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [key, hours] of Object.entries(parsed as Record<string, unknown>)) {
          const day = HOURS_DAY_ALIAS[key.trim().toLowerCase()];
          if (day && typeof hours === 'string' && hours.trim()) out[day] = hours.trim();
        }
        return Object.keys(out).length ? out : null;
      }
    } catch {
      // Fall through to free-text day parsing.
    }
  }
  const re =
    /\b(mon(?:day)?|tue(?:s(?:day)?)?|wed(?:nesday)?|thu(?:rs(?:day)?)?|fri(?:day)?|sat(?:urday)?|sun(?:day)?)\b\s*[:-]?\s*([^,;\n]+)/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(trimmed))) {
    const day = HOURS_DAY_ALIAS[match[1].toLowerCase()];
    const hours = match[2].trim();
    if (day && hours) out[day] = hours;
  }
  return Object.keys(out).length ? out : null;
}

// eslint-disable-next-line react-refresh/only-export-components
export function parseSocialsMap(value: string): Record<string, string> | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const out: Record<string, string> = {};
  if (trimmed.startsWith('{')) {
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [platform, url] of Object.entries(parsed as Record<string, unknown>)) {
          if (platform.trim() && typeof url === 'string' && url.trim()) {
            out[platform.trim()] = url.trim();
          }
        }
        return Object.keys(out).length ? out : null;
      }
    } catch {
      // Fall through to platform:url parsing.
    }
  }
  const chunks = trimmed
    .split(/[\n;]+|(?:,\s*(?=[A-Za-z][A-Za-z0-9._-]*\s*:))/)
    .map((part) => part.trim())
    .filter(Boolean);
  for (const chunk of chunks) {
    const sep = chunk.indexOf(':');
    if (sep < 1) continue;
    const platform = chunk.slice(0, sep).trim();
    const url = chunk.slice(sep + 1).trim();
    if (/^https?$/i.test(platform) || !platform || !url) continue;
    out[platform] = url;
  }
  return Object.keys(out).length ? out : null;
}

function pushContext(
  items: ContextItem[],
  seen: Set<string>,
  key: string,
  value: string | number | boolean | null | undefined,
): void {
  if (items.length >= CONTEXT_MAX) return;
  if (seen.has(key)) return;
  if (value === null || value === undefined) return;
  const text = typeof value === 'string' ? clip(value, CONTEXT_VALUE_MAX) : clip(String(value), CONTEXT_VALUE_MAX);
  if (!text) return;
  seen.add(key);
  items.push({ key, value: text });
}

/**
 * A field's resolved display state. Every field — `description` included — is a
 * network target on the shared route, so every state flows from the async
 * model-seam path. The states are deliberately distinct so an operator never
 * reads "loading" as "found nothing", or a failed request ("could not ask")
 * as an honest empty ("asked, nothing to ground").
 */
type FieldSuggestionState =
  | { kind: 'value'; candidate: string; source: string | null; sourceLinks: OfficialSourceLink[] }
  | { kind: 'none' }
  | { kind: 'refused'; reason: string }
  | { kind: 'loading' }
  | { kind: 'error' };

/** A field resolved at render into one of the four display states. */
interface ResolvedSuggestionField {
  key: string;
  label: string;
  state: FieldSuggestionState;
  /** Writes EXACTLY this one field; never verification state. */
  onAccept: (value: string) => void;
}

interface ResolvedSuggestionGroup {
  id: string;
  label: string;
  fields: ResolvedSuggestionField[];
  status: 'idle' | 'loading' | 'loaded' | 'error';
  /** Starts the group once; cached/loading/error groups are left untouched. */
  onOpen: () => void;
  /** Re-runs the shared-route fetch — wired to each error row's retry. */
  onRetry: () => void;
  /** Re-runs a completed group with the latest Section A context. */
  onRefresh: () => void;
}

interface OfficialSourceLink {
  url: string;
  hostname: string;
}

/** Keep only safe, absolute HTTP(S) links and deduplicate by normalized URL. */
// ── rest-of-§A #18: URL shape guard ───────────────────────────────────────
// Same parse rule as officialSourceLinks below — http(s) only. §A's four
// URL-bearing controls (Website, Booking URL, GBP, Socials) must never store
// a malformed URL as a brand-bible fact that generation then projects.
export function isValidHttpUrl(raw: string): boolean {
  const value = raw.trim();
  if (!value) return false;
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

const URL_HINT = 'Enter a full http(s) URL — e.g. https://example.com';

function officialSourceLinks(urls: Array<string | null | undefined>): OfficialSourceLink[] {
  const links: OfficialSourceLink[] = [];
  const seen = new Set<string>();
  for (const raw of urls) {
    if (typeof raw !== 'string' || !raw.trim()) continue;
    try {
      const parsed = new URL(raw.trim());
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') continue;
      const normalized = parsed.toString();
      if (seen.has(normalized)) continue;
      seen.add(normalized);
      links.push({ url: normalized, hostname: parsed.hostname });
    } catch {
      // Malformed source metadata is omitted; it never becomes a clickable URL.
    }
  }
  return links;
}

// ── Location field richness (SPEC-location-fields §2/§3/§4) ─────────────────
// T4 typed address parts, T5 kinded coverage, T2 read-mostly enrichment, T3
// conflict state. The columns/sidecars these read+write land in the schema
// lane's PR (feat/bb-location-fields — locf-be-builder). Until that lands the
// live API neither returns nor accepts them, so the section reaches them
// through the OPTIONAL, spec-shaped props on SectionEssentialsProps below
// (each defaulting to a derivation of what the API DOES carry) — the same seam
// pattern as `fetchFieldSuggestions`. Nothing here needs the backend PR to
// render; the writes are inert until the schema lane wires the real seams.

export type CoverageKind = 'city' | 'county' | 'region';

/** §2.1 typed address parts (backend columns pending — see note above). */
export interface LocationParts {
  street?: string | null;
  suite?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  /** §5.2 dedup: name of a parts-equal twin flagged for the Merge review step. */
  duplicateOf?: string | null;
}

/** §4.3 kinded coverage sibling `service_areas: [{name, kind}]`. */
export interface CoverageArea {
  name: string;
  /** null = un-migrated legacy string → plain chip (the lazy path, T5). */
  kind: CoverageKind | null;
}

/** §2.3 one source-owned tracking tier ROW (tier × number × call volume). */
export interface LocationTrackingTier {
  tier?: string | null;
  tracking_number?: string | null;
  call_volume?: number | null;
}

/** §2.3 read-mostly enrichment (sidecar, source='discovered'). */
export interface LocationEnrichment {
  gbp_rating?: number | null;
  gbp_review_count?: number | null;
  gbp_as_of?: string | null;
  place_id?: string | null;
  lat?: number | null;
  lng?: number | null;
  tracking?: LocationTrackingTier[];
}

/** §2.4 one OPEN per-field conflict row (resolved_at IS NULL). */
export interface FieldConflict {
  field_key: string;
  source_a: string;
  value_a: string;
  source_b: string;
  value_b: string;
  reason?: string | null;
}

/** The typed address part columns, with label + placeholder, in display order.
 *  Carl 2026-08-17: the Primary-only address-parts grid is unmounted; LocationList
 *  is the typed surface. Keep this table for #1670 re-attach. */
// eslint-disable-next-line react-refresh/only-export-components
export const ADDRESS_PARTS: ReadonlyArray<readonly [keyof LocationParts, string, string]> = [
  ['street', 'Street', 'e.g. 123 Main St'],
  ['suite', 'Suite / Unit', 'e.g. Suite 4'],
  ['city', 'City', 'e.g. Clayton'],
  ['state', 'State', 'e.g. MO'],
  ['zip', 'ZIP', 'e.g. 63105'],
];

/**
 * §4.1 derived display string — the single place the address is formatted:
 * `street[, suite], city, state zip`, else the legacy `address` fallback when
 * every part is empty (the lazy window). NEVER stored — this compute is what
 * T4 requires so there is no second editable store for the address.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function formatAddressDisplay(parts: LocationParts | undefined, fallback: string): string {
  const g = (v: string | null | undefined) => (v ?? '').trim();
  const street = g(parts?.street);
  const suite = g(parts?.suite);
  const city = g(parts?.city);
  const state = g(parts?.state);
  const zip = g(parts?.zip);
  if (!street && !suite && !city && !state && !zip) return fallback.trim();
  const line1 = [street, suite].filter(Boolean).join(', ');
  const stateZip = [state, zip].filter(Boolean).join(' ');
  const line2 = [city, stateZip].filter(Boolean).join(', ');
  return [line1, line2].filter(Boolean).join(', ');
}

/**
 * T4 one-column address-part write + caller-side change-guard. The address-parts
 * UI is unmounted (Carl 2026-08-17); #1670 re-attaches this to LocationList rows.
 */
// eslint-disable-next-line react-refresh/only-export-components
export async function writePart(
  partKey: keyof LocationParts,
  nextValue: string,
  currentValue: string,
  persist: (patch: Partial<LocationParts>) => Promise<unknown>,
): Promise<void> {
  const trimmed = nextValue.trim();
  if (trimmed === currentValue.trim()) return;
  await persist({ [partKey]: trimmed } as Partial<LocationParts>);
}

/**
 * §T5 auto-guess a coverage kind from the entered name. Order is load-bearing:
 * "County" wins over the ", ST" city suffix (a county can carry a state
 * suffix), and an un-guessable bare name stays NULL (the lazy chip). One-click
 * correction lets the operator override any guess.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function guessCoverageKind(name: string): CoverageKind | null {
  const s = name.trim();
  if (!s) return null;
  if (/\bcounty\b/i.test(s)) return 'county';
  if (
    /\bstatewide\b/i.test(s) ||
    /\b(?:greater|midwest|northeast|southeast|southwest|northwest|tri-?state|bay area|metro area|region)\b/i.test(s)
  )
    return 'region';
  if (/,\s*[A-Za-z]{2}\b/.test(s)) return 'city';
  return null;
}

/**
 * §T3 the field's display state. Extends the file's five-state suggestion
 * vocabulary (value/none/refused/loading/error) with a SIXTH, `conflict`. An
 * open conflict is a HARD GATE: it wins over value/none so the field yields no
 * editable value — never conflated with an honest empty (`none`) or a failed
 * request (`error`).
 */
export type LocationFieldState =
  | { kind: 'value'; value: string }
  | { kind: 'none' }
  | { kind: 'refused'; reason: string }
  | { kind: 'loading' }
  | { kind: 'error' }
  | { kind: 'conflict'; conflict: FieldConflict };

// eslint-disable-next-line react-refresh/only-export-components
export function locationFieldState(
  fieldKey: string,
  currentValue: string,
  conflicts: FieldConflict[],
): LocationFieldState {
  const conflict = conflicts.find((c) => c.field_key === fieldKey);
  if (conflict) return { kind: 'conflict', conflict };
  return currentValue.trim() ? { kind: 'value', value: currentValue } : { kind: 'none' };
}

/**
 * §T3 conflict row — two disagreeing sources + the reason + a Resolve stub, and
 * deliberately NO editable value (the hard gate). Reuses the file's
 * status-error token; distinct from the empty/error suggestion states.
 * Carl 2026-08-17: unmounted from §A render; #1670 re-attaches to LocationList.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function ConflictRow({ conflict, onResolve }: { conflict: FieldConflict; onResolve: () => void }) {
  return (
    <div
      data-testid={`bb-essentials-conflict-${conflict.field_key}`}
      className="rounded-lg border border-[hsl(var(--status-error))]/50 bg-[hsl(var(--status-error))]/5 px-3 py-2.5"
    >
      <div className="mb-1 flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[hsl(var(--status-error))]">
          Conflict — needs resolution
        </span>
        <button
          type="button"
          data-testid={`bb-essentials-conflict-resolve-${conflict.field_key}`}
          onClick={onResolve}
          className="rounded-full border border-coral-dark bg-white px-2.5 py-0.5 text-[11px] font-semibold text-coral-dark hover:bg-coral-light/40"
        >
          Resolve
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2 text-[12px]">
        <div className="rounded border border-border bg-card px-2 py-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{conflict.source_a}</div>
          <div className="text-foreground">{conflict.value_a}</div>
        </div>
        <div className="rounded border border-border bg-card px-2 py-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">{conflict.source_b}</div>
          <div className="text-foreground">{conflict.value_b}</div>
        </div>
      </div>
      {conflict.reason && <p className="mt-1 text-[11px] italic text-muted-foreground">{conflict.reason}</p>}
    </div>
  );
}

/**
 * §T2 read-mostly enrichment strip — source-owned, display-only, NEVER an
 * input. Tracking numbers render tiered here (tier × number × volume) and are
 * NEVER placed in a contact input: the tracking-number-in-a-contact-slot
 * invariant is enforced at the write seam, and this UI simply never offers the
 * promotion.
 */
function EnrichmentStrip({ enrichment, onRefresh }: { enrichment: LocationEnrichment; onRefresh: () => void }) {
  const hasRating = enrichment.gbp_rating != null || enrichment.gbp_review_count != null;
  const hasCoords = enrichment.lat != null && enrichment.lng != null;
  return (
    <div data-testid="bb-essentials-enrichment" className="mt-3.5 rounded-lg border border-border bg-teal-light/30">
      <div className="flex items-center justify-between border-b border-border/60 px-3.5 py-2.5">
        <span className="text-[13px] font-semibold">Enrichment · read-only</span>
        <button
          type="button"
          data-testid="bb-essentials-enrichment-refresh"
          onClick={onRefresh}
          className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-2.5 py-0.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
        >
          <RefreshCw className="size-3" />
          Refresh
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2.5 p-3.5 text-[12px]">
        {hasRating && (
          <div data-testid="bb-essentials-enrichment-rating">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Google rating</div>
            <div className="text-foreground">
              {enrichment.gbp_rating ?? '—'}
              {enrichment.gbp_review_count != null && ` (${enrichment.gbp_review_count})`}
              {enrichment.gbp_as_of && <span className="text-muted-foreground"> · as of {enrichment.gbp_as_of}</span>}
            </div>
          </div>
        )}
        {enrichment.place_id && (
          <div data-testid="bb-essentials-enrichment-place">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Place ID</div>
            <div className="truncate text-foreground">{enrichment.place_id}</div>
          </div>
        )}
        {hasCoords && (
          <div data-testid="bb-essentials-enrichment-coords">
            <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Coordinates</div>
            <div className="text-foreground">
              {enrichment.lat}, {enrichment.lng}
            </div>
          </div>
        )}
      </div>
      {enrichment.tracking && enrichment.tracking.length > 0 && (
        <div data-testid="bb-essentials-enrichment-tracking" className="border-t border-border/60 px-3.5 py-2.5">
          <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Call tracking · by tier</div>
          <div className="space-y-1">
            {enrichment.tracking.map((t, i) => (
              <div
                key={`${t.tier ?? 'tier'}-${i}`}
                data-testid={`bb-essentials-enrichment-tracking-${i}`}
                className="flex items-center gap-2 text-[12px]"
              >
                <span className="rounded-full bg-teal-light px-2 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-teal-dark">
                  {t.tier ?? 'tier'}
                </span>
                <span className="font-medium text-foreground">{t.tracking_number}</span>
                {t.call_volume != null && <span className="text-muted-foreground">· {t.call_volume} calls</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * §A-local multi-field suggestion panel. One button per subsection group; the
 * dropdown lists every covered field in one of four states — a grounded value
 * (with a per-field Accept), the explicit no-suggestion state, a loading state
 * while the model seam is pending, or an honest failure state when it rejects.
 * Built from primitives already used in this file (no new visual language or
 * dependencies). Every field is mapped through the shared structured route.
 */
function FieldSuggestionGroup({ group }: { group: ResolvedSuggestionGroup }) {
  const [open, setOpen] = useState(false);
  const groundedCount = group.fields.filter((f) => f.state.kind === 'value').length;
  const refusedCount = group.fields.filter((f) => f.state.kind === 'refused').length;
  const anyLoading = group.fields.some((f) => f.state.kind === 'loading');
  const anyError = group.fields.some((f) => f.state.kind === 'error');
  // Panel framing so a mostly-declined/empty panel reads as deliberate, never as
  // broken. Distinguishes failure and loading from honest declines and empties.
  const note = anyError
    ? 'Some suggestions couldn’t be loaded — that’s a failed request, not an empty result. Retry below.'
    : anyLoading
      ? 'Checking…'
      : groundedCount
        ? `${groundedCount} suggestion${groundedCount === 1 ? '' : 's'} — review before using.`
        : refusedCount
          ? 'These were declined — each field shows why. Nothing is broken.'
          : 'No suggestions yet — add more Section A details and they’ll appear here. Nothing is missing.';
  return (
    <div className="relative" data-testid={`bb-suggest-group-${group.id}`}>
      <button
        type="button"
        data-testid={`bb-suggest-btn-${group.id}`}
        aria-label={`AI Suggest for ${group.label}`}
        onClick={() => {
          if (!open) group.onOpen();
          setOpen(!open);
        }}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-teal-light text-teal-dark border border-teal/40 text-[12px] font-semibold cursor-pointer hover:bg-teal-light/80"
      >
        <Lightbulb className="size-3.5" />
        AI Suggest
      </button>
      {open && (
        <div
          data-testid={`bb-suggest-panel-${group.id}`}
          role="dialog"
          aria-label={`${group.label} suggestions`}
          className="absolute right-0 z-20 mt-1.5 w-[340px] rounded-lg border border-border bg-card p-2.5 shadow-lg"
        >
          <div className="mb-1 flex items-center justify-between">
            <span className="text-[11px] font-semibold text-foreground">{group.label}</span>
            <div className="flex items-center gap-1.5">
              {group.status === 'loaded' && (
                <button
                  type="button"
                  data-testid={`bb-suggest-refresh-${group.id}`}
                  onClick={() => group.onRefresh()}
                  className="text-[11px] font-semibold text-muted-foreground underline hover:text-foreground"
                >
                  Refresh
                </button>
              )}
              <button
                type="button"
                aria-label="Close suggestions"
                onClick={() => setOpen(false)}
                className="grid place-items-center w-5 h-5 rounded text-muted-foreground hover:text-foreground"
              >
                <X className="size-3.5" />
              </button>
            </div>
          </div>
          <p
            data-testid={`bb-suggest-note-${group.id}`}
            className="mb-1.5 flex items-start gap-1 text-[11px] leading-snug text-muted-foreground"
          >
            <Info className="mt-[1px] size-3 flex-shrink-0" />
            <span>{note}</span>
          </p>
          <div className="divide-y divide-border/60">
            {group.fields.map((f) => (
              <div key={f.key} data-testid={`bb-suggest-field-${f.key}`} className="py-2">
                <div className="text-[11px] font-semibold text-foreground">{f.label}</div>
                {f.state.kind === 'value' && (
                  <div className="mt-1">
                    <div className="text-[13px] text-foreground leading-relaxed">{f.state.candidate}</div>
                    <div className="mt-1 flex items-center gap-2">
                      <button
                        type="button"
                        data-testid={`bb-suggest-accept-${f.key}`}
                        onClick={() => {
                          const s = f.state;
                          if (s.kind === 'value') {
                            f.onAccept(s.candidate);
                            setOpen(false);
                          }
                        }}
                        className="px-2.5 py-1 rounded-md text-[12px] font-semibold text-white bg-coral hover:bg-coral-hover border border-coral cursor-pointer"
                      >
                        Use this
                      </button>
                      {f.state.source && (
                        <span
                          data-testid={`bb-suggest-source-${f.key}`}
                          className="text-[11px] text-muted-foreground"
                        >
                          {f.state.source}
                        </span>
                      )}
                    </div>
                    {f.state.sourceLinks.length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1 text-[11px]">
                        {f.state.sourceLinks.map((source) => (
                          <a
                            key={source.url}
                            href={source.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label={`Unverified official source ${source.hostname}, opens in new tab`}
                            className="text-teal-dark underline hover:text-foreground"
                          >
                            {source.hostname}
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                {f.state.kind === 'none' && (
                  <div
                    data-testid={`bb-suggest-none-${f.key}`}
                    className="mt-1 text-[12px] italic text-muted-foreground"
                  >
                    No suggestion — nothing to ground it in
                  </div>
                )}
                {f.state.kind === 'refused' && (
                  <div
                    data-testid={`bb-suggest-refused-${f.key}`}
                    className="mt-1 text-[12px] italic text-muted-foreground"
                  >
                    Declined — {f.state.reason}
                  </div>
                )}
                {f.state.kind === 'loading' && (
                  <div
                    data-testid={`bb-suggest-loading-${f.key}`}
                    className="mt-1 flex items-center gap-1.5 text-[12px] text-muted-foreground"
                  >
                    <Loader2 className="size-3 animate-spin" />
                    Checking…
                  </div>
                )}
                {f.state.kind === 'error' && (
                  <div
                    data-testid={`bb-suggest-error-${f.key}`}
                    className="mt-1 flex items-center gap-1.5 text-[12px] text-[hsl(var(--status-error))]"
                  >
                    Couldn’t load — could not reach suggestions.
                    <button
                      type="button"
                      data-testid={`bb-suggest-retry-${f.key}`}
                      onClick={() => group.onRetry()}
                      className="font-semibold underline cursor-pointer hover:text-foreground"
                    >
                      Retry
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export interface SectionEssentialsProps {
  sectionRef: (el: HTMLElement | null) => void;
  draft: BrandBibleDraft;
  set: <K extends keyof BrandBibleDraft>(key: K, value: BrandBibleDraft[K]) => void;
  draftRowsForTables: (tables: BbTable[]) => DraftRow[];
  tenantInfo: { website?: string | null } | undefined;
  currentTenant: string | null;
  canVerifyV2: boolean;
  goToDraftSection: (row: DraftRow) => void;
  onDraftConfirmed: (row: DraftRow) => void;
  sectionLabelFor: (row: DraftRow) => string;
  /** T1 (draft deep-link): kept for the parked panel contract; the current
   *  EnrichmentDraftsPanel on this branch does not accept them yet, so they are
   *  intentionally NOT forwarded (re-wire when the panel lands focus support). */
  focusEntryId?: string | null;
  onDraftDiscarded?: (row: DraftRow) => void;
  businessNameDup: AttentionRow | null;
  verifyV2: (table: BbTable, id: string, approveForPublic?: boolean) => Promise<void>;
  identity: ReturnType<typeof useSingleton<'identity'>>;
  identityIsDraft: boolean;
  verifyIdentity: () => void;
  saveIdentity: (patch: Partial<IdentityPayload>) => Promise<unknown>;
  saveOwner: (patch: Partial<NonNullable<IdentityPayload['owner']>>) => void;
  identitySaveErrorToast: (field: string) => (e: unknown) => void;
  socialsMap: Record<string, string | null>;
  socialDraft: string;
  setSocialDraft: (v: string) => void;
  addSocial: () => void;
  removeSocial: (platform: string) => void;
  syncServiceLocationsV2: (next: string[]) => Promise<unknown>;
  primaryLocation: BbEntry<'locations'> | null;
  savePrimaryLocation: (patch: Partial<BbPayloadMap['locations']>) => Promise<unknown>;
  // ── Location field-richness seams (SPEC-location-fields) ──────────────────
  // All optional: BrandBiblePage (read-only for this lane) does not pass them
  // yet, and the backend columns/sidecars land in feat/bb-location-fields
  // (locf-be-builder). Each defaults to a spec-shaped derivation of the current
  // API so the UI renders now; the real reads/writes wire in with the backend PR.
  /** §2.1 typed address parts for the primary location. Default: none → the
   *  parts seed empty and the display falls back to the legacy `address`. */
  locationParts?: LocationParts;
  /** Writes exactly ONE part column (§3.1 registry). Default routes through
   *  `savePrimaryLocation` with a cast — inert until the columns land. */
  savePrimaryLocationPart?: (patch: Partial<LocationParts>) => Promise<unknown>;
  /** §4.3 kinded coverage areas. Carl 2026-08-17: the duplicate T5 chip row
   *  under LocationList is gone. Kinds will live ON ComboboxLocations chips
   *  when #1670 lands (`coverage_kind` 422s until then). */
  coverageAreas?: CoverageArea[];
  /** Persists a coverage row's kind. Unused on this surface until kinds move
   *  onto ComboboxLocations with #1670. */
  saveCoverageKind?: (name: string, kind: CoverageKind) => Promise<unknown>;
  /** §2.3 read-mostly enrichment for the primary location. Default: none. */
  locationEnrichment?: LocationEnrichment | null;
  /** Refresh affordance stub — re-pull from the enrichment source (deferred,
   *  §6: cadence/scheduler is a non-blocking TODO). Default: no-op. */
  refreshLocationEnrichment?: () => void;
  /** §2.4 open per-field conflicts on the primary location. Default: none. */
  fieldConflicts?: FieldConflict[];
  /** Resolve affordance stub — opens the resolution flow (client answer / pick).
   *  Default: no-op; the real resolution write is a backend-PR concern. */
  resolveFieldConflict?: (fieldKey: string) => void;
  /** Shared-route seam injection point — defaults to `requestModelFieldSuggestions`
   *  (real HTTP). Tests inject timing/responses; there is no no-op default. */
  fetchFieldSuggestions?: FetchFieldSuggestions;
}

export function SectionEssentials({
  sectionRef,
  draft,
  set,
  draftRowsForTables,
  tenantInfo,
  currentTenant,
  canVerifyV2,
  goToDraftSection,
  onDraftConfirmed,
  sectionLabelFor,
  businessNameDup,
  verifyV2,
  identity,
  identityIsDraft,
  verifyIdentity,
  saveIdentity,
  syncServiceLocationsV2,
  socialsMap,
  socialDraft,
  setSocialDraft,
  primaryLocation,
  savePrimaryLocation,
  locationEnrichment,
  refreshLocationEnrichment,
  fetchFieldSuggestions,
}: SectionEssentialsProps) {
  // NOTE: the parked build read `unverifyEntry` off the wizard store and
  // rendered per-row Unverify buttons. This branch's store has no unverifyEntry
  // action, so the Unverify affordance is omitted — verify state here is
  // verify-only until the store action lands.
  const licenseInsuranceCredentials = useBrandBibleWizardStore(selectEntries('credentials')).filter(
    (c) => c.deleted_at === null && (c.kind === 'license' || c.kind === 'insurance'),
  );

  // ── §A explicit-save buffer ───────────────────────────────────────────────
  // Every §A field edit lands here instead of the wire (see essentialsBuffer).
  // Only Verify / unverify / "Verify all" stay immediate below. The page's
  // callbacks are replayed by save() once, in a fixed order; discard() clears
  // the buffer and the UI falls back to the store view.
  const [buffer, setBuffer] = useState<EssentialsBuffer>(EMPTY_ESSENTIALS_BUFFER);
  // Bumped by discard() so the uncontrolled IdentityField inputs (dba, owner,
  // location details, …) remount with their stored defaultValue — a cleared
  // buffer alone can't reset an uncontrolled input's DOM value.
  const [discardNonce, setDiscardNonce] = useState(0);
  const commitDraftFields = useBbDraftCommit();
  // L9 #9: the service-areas chips row is unmounted from §A — coverage lives
  // in the AlsoServingBand inside LocationList (metro rows), and location
  // rows are the geographic truth. The auto-feed effect that mirrored
  // location cities into the buffer's service_locations list is gone with
  // the mount; buffer.serviceLocations is still written by the AI-suggest
  // path and replayed by save(), so no write path is orphaned.

  const [verifyAllState, setVerifyAllState] = useState<'idle' | 'running' | 'success' | 'partial'>('idle');
  const [verifyAllResult, setVerifyAllResult] = useState<{ succeeded: number; failed: number } | null>(null);
  // rest-of-§A #18: rejected-commit feedback for the URL-bearing controls.
  const [bookingUrlError, setBookingUrlError] = useState(false);
  const [socialError, setSocialError] = useState<string | null>(null);

  // Race guard for the hours JSONB map. The backend REPLACES the whole hours
  // column (`SET hours = CAST(:hours AS jsonb)`), so every write must carry the
  // COMPLETE merged map. Two day-commits fired back-to-back — before the async
  // save resolves and the `primaryLocation` prop re-renders with the first
  // write — would otherwise BOTH read the same stale `primaryLocation.hours`,
  // and the second write would drop the first day. To a user that presents
  // exactly as "my hours edits reverted on reload". This ref holds the most
  // recently WRITTEN map so each commit merges onto the latest local value, not
  // the stale prop. It is seeded from the row and re-seeded ONLY when the
  // primary-location id changes — never on every render, because a mid-race
  // prop is stale and re-seeding from it would reintroduce the bug; keying on
  // id also stops one location's hours leaking into another after a row switch.
  // Deliberately NOT the other three fields: radius/tracking_phone/gbp_url are
  // scalars whose writes carry the full new value (no merge), so a stale prop
  // there can at worst skip a redundant write — it cannot drop data.
  const hoursRef = useRef<Record<string, string>>(primaryLocation?.hours ?? {});
  const hoursSeededIdRef = useRef<string | null>(primaryLocation?.id ?? null);
  // Deliberate render-time reseed, keyed on the row id (never every render) —
  // see the race-guard comment above; the react-hooks/refs rule can't see that
  // this is the sanctioned adjust-during-render pattern applied to refs.
  // eslint-disable-next-line react-hooks/refs
  if (primaryLocation && hoursSeededIdRef.current !== primaryLocation.id) {
    // Row switched (or first mount of a row): re-seed from the new row's map.
    // eslint-disable-next-line react-hooks/refs
    hoursRef.current = primaryLocation.hours ?? {};
    // eslint-disable-next-line react-hooks/refs
    hoursSeededIdRef.current = primaryLocation.id;
  }

  // Carl 2026-08-17: the Primary-only address-parts UI is unmounted. Keep
  // `writePart` / ADDRESS_PARTS / ConflictRow / locationFieldState exported
  // for #1670 re-attach to LocationList. `draft.address` stays the AI-suggest
  // target + legacy fallback.

  // Non-mutating key removal for building the post-save buffer (kept edits)
  // that failed stay; succeeded edits drop). Generic so every sub-map reuses it.
  const omitKey = <T extends Record<string, unknown>>(obj: T, key: string): T => {
    const out = { ...obj };
    delete out[key];
    return out;
  };

  // save() — replay the buffer through the page callbacks in a fixed order,
  // awaiting each so a rejection keeps that field dirty and the footer armed.
  // Ops that succeed drop out of the buffer; ops that fail stay in it, so a
  // partial failure keeps exactly the unsaved work (mirrors §C/§D).
  const save = useCallback(async (): Promise<DirtyFormSaveResult> => {
    const failures: string[] = [];
    let next = buffer;

    // v2-active is read LIVE from the store (a fresh, non-deleted bb_identity
    // row), never a captured render prop: it flips async after mount, and the
    // page's commitDraftFields seam is memoized on `draft` (which §A no longer
    // mutates until save), so its own isV2Active can be stale by the time we
    // commit. Deciding here keeps the v2/non-v2 branch correct regardless.
    const hasV2Identity = (useBrandBibleWizardStore.getState().entries.identity ?? []).some(
      (e) => e.deleted_at === null,
    );

    // The draft-backed scalars are DUAL-WRITE: a flat legacy column AND a v2
    // home. The v2 half is replayed NARROWLY, one buffered field as its own
    // saveIdentity / savePrimaryLocation / diff-sync patch, so exactly the
    // edited fields reach the wire — NOT the page's whole-draft pushDraftToV2,
    // which would also re-emit every unedited draft field and defer the edit
    // behind a coalesced flush. This also makes address a single write (never a
    // duplicate cold primary-location create) since commitDraftFields (which
    // would fire its own pushDraftToV2) is skipped for v2 tenants below.
    let draftGroupOk = true;
    const failDraft = (label: string) => {
      draftGroupOk = false;
      failures.push(label);
    };

    const IDENTITY_HALF: Partial<Record<DraftScalarField, keyof IdentityPayload>> = {
      business_name: 'legal_name',
      website: 'website',
      phone: 'phone',
      email: 'email',
      description: 'brand_overview',
    };
    for (const field of Object.keys(buffer.draft) as DraftScalarField[]) {
      const v2Key = IDENTITY_HALF[field];
      if (!v2Key) continue; // address handled just below
      if (identityIsDraft) continue; // an unconfirmed draft identity is never written in place
      try {
        await saveIdentity({ [v2Key]: buffer.draft[field] });
      } catch {
        failDraft(field);
      }
    }
    // rest-of-§A ruling #16: `address` is never a draft write — no input,
    // no AI target. LocationList (typed parts + composed address) is the
    // address owner; a stray legacy address key would be a second store.
    if (buffer.serviceLocations !== undefined) {
      try {
        await syncServiceLocationsV2(buffer.serviceLocations);
      } catch {
        failDraft('service locations');
      }
    }

    // The flat legacy columns are the SOLE write path only for a NON-v2 tenant;
    // commit them through the page's commitDraftFields seam (legacy PUT with
    // includeLetters=['A']). For a v2 tenant the flat columns are legacy cruft
    // the app never reads back (the legacy PUT 409s server-side), and the narrow
    // v2 writes above already persisted every field — so we skip the seam
    // entirely, which also avoids its stale-isV2Active closure firing a
    // redundant legacy PUT. A null seam on a non-v2 tenant fails loudly.
    if (!hasV2Identity) {
      const draftFields: Record<string, unknown> = { ...buffer.draft };
      if (buffer.serviceLocations !== undefined) draftFields.service_locations = buffer.serviceLocations;
      if (Object.keys(draftFields).length > 0) {
        if (!commitDraftFields) {
          return { success: false, error: 'Save is unavailable right now — reload the page and try again.' };
        }
        try {
          const ok = await commitDraftFields(draftFields, 'A');
          if (!ok) failDraft('business details');
        } catch {
          failDraft('business details');
        }
      }
    }

    if (draftGroupOk) {
      // Sync the page draft to the just-saved values. Critical for v2 tenants,
      // where commitDraftFields is skipped above: without this, draft keeps the
      // stale pre-edit values and a LATER debounced autosave (fired by some
      // OTHER section's edit) would pushDraftToV2 those stale §A fields and
      // clobber what we just wrote to bb_identity/bb_locations. set() is safe
      // here — §A owns these flat columns (bbLegacyFieldOwnership), so they are
      // stripped from the autosave hash and this write triggers no persist of
      // its own. For non-v2 tenants commitDraftFields already did the setDraft;
      // repeating it is a harmless idempotent merge.
      for (const field of Object.keys(buffer.draft) as DraftScalarField[]) {
        set(field, buffer.draft[field] ?? '');
      }
      if (buffer.serviceLocations !== undefined) set('service_locations', buffer.serviceLocations);
      next = { ...next, draft: {}, serviceLocations: undefined };
    }

    // 2) bb_identity scalars with NO flat echo — each replayed as its OWN narrow
    //    saveIdentity patch, sequentially, so the singleton's create-in-flight
    //    ref (page saveIdentity) is never raced by a parallel fan-out.
    for (const field of Object.keys(buffer.identity) as IdentityScalarField[]) {
      if (identityIsDraft) break;
      try {
        await saveIdentity(coerceIdentityField(field, buffer.identity[field] ?? ''));
        next = { ...next, identity: omitKey(next.identity, field) };
      } catch {
        failures.push(field);
      }
    }

    // 3) owner — a merge-patch of CHANGED subkeys only (buffer.owner already IS
    //    the delta; never a {...current, ...patch} spread). One awaited call.
    if (Object.keys(buffer.owner).length > 0 && !identityIsDraft) {
      try {
        await saveIdentity({ owner: buffer.owner });
        next = { ...next, owner: {} };
      } catch {
        failures.push('owner');
      }
    }

    // 4) socials — one narrow merge (url) / delete (null) per platform; never a
    //    whole-map PUT (Appendix A.5 merge-patch discipline).
    for (const platform of Object.keys(buffer.socials)) {
      if (identityIsDraft) break;
      try {
        await saveIdentity({ socials: { [platform]: buffer.socials[platform] } });
        next = { ...next, socials: omitKey(next.socials, platform) };
      } catch {
        failures.push(platform);
      }
    }

    // 5) primary-location columns (no draft key) — a SINGLE merged write; hours
    //    is already a complete map in the buffer, so one call is correct and the
    //    box only renders when a primary row exists, so this is always a warm
    //    PATCH (never a second cold create racing the address create above).
    if (Object.keys(buffer.location).length > 0) {
      try {
        await savePrimaryLocation(coerceLocationPatch(buffer));
        next = { ...next, location: {} };
      } catch {
        failures.push('location details');
      }
    }

    setBuffer(next);
    if (failures.length > 0) {
      return {
        success: false,
        error: `Couldn't save ${failures.join(', ')} — your edits are still here, try again.`,
      };
    }
    return { success: true };
    // saveIdentity/savePrimaryLocation are re-created each page render but only
    // close over refs/state the buffer already tracks; keying on them would
    // re-register every render. commitDraftFields IS memoized on the page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buffer, commitDraftFields]);

  const discard = useCallback(() => {
    setBuffer(EMPTY_ESSENTIALS_BUFFER);
    // Remount the uncontrolled IdentityField inputs back to their stored value.
    setDiscardNonce((n) => n + 1);
  }, []);

  // The registration object MUST change identity whenever the buffer changes:
  // the registry only notifies on register/save/discard, so a registration
  // memoized on [] would freeze the footer's count while the user typed.
  const registration = useMemo<BbSectionSaveRegistration>(
    () => ({
      isDirty: () => bufferIsDirty(buffer),
      save,
      discard,
      changeCount: () => bufferChangeCount(buffer),
    }),
    [buffer, save, discard],
  );
  useRegisterBbSectionSave('A', registration);

  // Buffer isolation. A tenant switch makes the whole section fresh — drop every
  // buffered edit so one tenant's unsaved work can never leak into another
  // (SectionEssentials stays mounted across a tenant change, so the buffer would
  // otherwise persist). Fires harmlessly on mount (buffer already empty).
  useEffect(() => {
    setBuffer(EMPTY_ESSENTIALS_BUFFER);
    setDiscardNonce((n) => n + 1);
  }, [currentTenant]);
  // The primary-location row's identity changed within a tenant: its buffered
  // columns (radius/tracking_phone/gbp_url/hours) belonged to the previous row,
  // so drop just those — the uncontrolled inputs are keyed by the row id and
  // re-seed on their own.
  useEffect(() => {
    setBuffer((b) => (Object.keys(b.location).length ? { ...b, location: {} } : b));
  }, [primaryLocation?.id]);

  // Socials render from the stored map with buffered deltas applied; add/remove
  // stage a narrow per-key merge/delete (never a whole-map PUT). `socialDraft`
  // stays page-owned input scratch — parsed here the same way the page's
  // addSocial did, but the result lands in the buffer instead of the wire.
  const effectiveSocials = bufferedSocials(buffer, socialsMap);
  const bufferSocialFromDraft = () => {
    const raw = socialDraft.trim();
    if (!raw) return;
    const sep = raw.indexOf(':');
    const platform = (sep > -1 ? raw.slice(0, sep) : raw).trim();
    const url = (sep > -1 ? raw.slice(sep + 1) : '').trim();
    if (!platform) return;
    // rest-of-§A #18: no free-junk channel — the value stored must be a real
    // http(s) URL, not the platform name echoed back at itself.
    if (!isValidHttpUrl(url)) {
      setSocialError(`${platform}: enter a full URL — e.g. ${platform}: https://…`);
      return;
    }
    setSocialError(null);
    setBuffer((b) => setSocial(b, platform, url, socialsMap));
    setSocialDraft('');
  };

  // ── §A per-field AI suggestions ──────────────────────────────────────────
  // Three subsection-local buttons (see SECTION_A_SUGGESTION_GROUPS) cover
  // every editable §A field through the shared structured route.
  // Gate (product-owner requirement): suggestion buttons are unavailable until
  // the tenant's website is set — via `draft.website` (seeded on load, updated
  // per keystroke), NOT `tenantInfo.website` (an Array.find that is undefined
  // during load and for any unmatched tenant, which would silently disable them).
  const websiteSet = bufferedDraft(buffer, 'website', draft.website).trim() !== '';
  // §A AI Suggest is release-gated behind FEATURE_FLAGS.brandBibleAiSuggest
  // (default OFF; the /brand-bible/ai-suggest backend route ships behind the same
  // gate). Same guard idiom as AISuggest.tsx:76 — flag off renders NOTHING
  // suggest-related, so no FieldSuggestionGroup button mounts and its click-driven
  // fetch seam never fires (there is no auto-fetch effect to short-circuit).
  const suggestUiEnabled = FEATURE_FLAGS.brandBibleAiSuggest && websiteSet;

  // Default fetcher is the real HTTP seam. Tests may inject `fetchFieldSuggestions`.
  const fetchSuggestions: FetchFieldSuggestions =
    fetchFieldSuggestions ?? ((request) => requestModelFieldSuggestions(request, currentTenant));

  const tenantContext = useMemo<ContextItem[]>(() => {
    const items: ContextItem[] = [];
    const seen = new Set<string>();
    pushContext(items, seen, 'tenant_id', currentTenant);
    return items;
  }, [currentTenant]);

  // Grounding context: every populated §A field, unique keys, empties dropped,
  // ≤24 items / 1500 chars. Keys must not collide with tenant_context.
  const sectionContext = useMemo<ContextItem[]>(() => {
    const items: ContextItem[] = [];
    const seen = new Set<string>(tenantContext.map((item) => item.key));
    // hoursRef holds the latest WRITTEN map (see the race guard above); reading
    // it here keeps context honest while a save is still in flight.
    // eslint-disable-next-line react-hooks/refs
    const hoursMap = primaryLocation?.hours ?? hoursRef.current;
    const onCamera = identity.entry?.owner?.on_camera_ok;
    pushContext(items, seen, 'business_name', draft.business_name);
    pushContext(items, seen, 'dba', identity.entry?.dba);
    pushContext(items, seen, 'founded_year', identity.entry?.founded_year);
    pushContext(items, seen, 'team_size', identity.entry?.team_size);
    pushContext(items, seen, 'website', draft.website);
    pushContext(items, seen, 'owner_name', identity.entry?.owner?.name);
    pushContext(items, seen, 'owner_title', identity.entry?.owner?.title);
    pushContext(items, seen, 'on_camera_ok', typeof onCamera === 'boolean' ? String(onCamera) : undefined);
    pushContext(items, seen, 'phone', draft.phone);
    pushContext(items, seen, 'email', draft.email);
    pushContext(items, seen, 'booking_url', identity.entry?.booking_url);
    pushContext(items, seen, 'socials', formatSocials(socialsMap));
    pushContext(items, seen, 'service_locations', draft.service_locations.join(', '));
    pushContext(items, seen, 'description', draft.description);
    pushContext(items, seen, 'address', draft.address);
    pushContext(items, seen, 'metro', primaryLocation?.metro);
    pushContext(items, seen, 'service_radius_miles', primaryLocation?.service_radius_miles);
    pushContext(items, seen, 'tracking_phone', primaryLocation?.tracking_phone);
    pushContext(items, seen, 'gbp_url', primaryLocation?.gbp_url);
    pushContext(items, seen, 'hours', formatHours(hoursMap));
    return items;
  }, [draft, identity.entry, primaryLocation, socialsMap, tenantContext]);

  const existingFor = (key: string): string[] => {
    const onCamera = identity.entry?.owner?.on_camera_ok;
    switch (key) {
      case 'business_name':
        return boundedExisting([draft.business_name]);
      case 'dba':
        return boundedExisting([identity.entry?.dba]);
      case 'founded_year':
        return boundedExisting([identity.entry?.founded_year?.toString()]);
      case 'team_size':
        return boundedExisting([identity.entry?.team_size?.toString()]);
      case 'website':
        return boundedExisting([draft.website]);
      case 'owner_name':
        return boundedExisting([identity.entry?.owner?.name]);
      case 'owner_title':
        return boundedExisting([identity.entry?.owner?.title]);
      case 'on_camera_ok':
        return typeof onCamera === 'boolean' ? [String(onCamera)] : [];
      case 'phone':
        return boundedExisting([draft.phone]);
      case 'email':
        return boundedExisting([draft.email]);
      case 'booking_url':
        return boundedExisting([identity.entry?.booking_url]);
      case 'socials':
        return boundedExisting(
          Object.entries(socialsMap).map(([platform, url]) => (url ? `${platform}: ${url}` : '')),
        );
      case 'service_locations':
        return boundedExisting(draft.service_locations);
      case 'description':
        return boundedExisting([draft.description]);
      case 'service_radius_miles':
        return boundedExisting([primaryLocation?.service_radius_miles?.toString()]);
      case 'tracking_phone':
        return boundedExisting([primaryLocation?.tracking_phone]);
      case 'gbp_url':
        return boundedExisting([primaryLocation?.gbp_url]);
      case 'hours':
        return boundedExisting(
          Object.entries(primaryLocation?.hours ?? hoursRef.current).map(([day, hours]) => `${day}: ${hours}`),
        );
      default:
        return [];
    }
  };

  // Per-BUTTON fetch state. Each button fires ONE request carrying only its own
  // fields as targets, including Brand overview.
  // output_key === target_field === field key, unique within a request.
  type GroupFetch =
    | { status: 'idle' | 'loading' | 'error' }
    | {
        status: 'loaded';
        values: Record<string, { value: string; source: string | null; sourceLinks: OfficialSourceLink[] }>;
        refusals: Record<string, string>;
      };
  const [groupFetches, setGroupFetches] = useState<Record<string, GroupFetch>>({});
  // Synchronous lifecycle mirror used as a click guard. React state updates are
  // batched, so state alone cannot stop a double/close/reopen click from issuing
  // duplicate requests before the loading render commits.
  const groupStatusRef = useRef<Record<string, GroupFetch['status']>>({});

  const fetchGroup = (groupId: string, force = false) => {
    const group = SECTION_A_SUGGESTION_GROUPS.find((g) => g.id === groupId);
    if (!group) return;
    const currentStatus = groupStatusRef.current[groupId] ?? 'idle';
    if (currentStatus === 'loading') return;
    // Opening is lazy and cache-aware: only a never-requested group starts.
    // Error and loaded states change only through explicit Retry/Refresh.
    if (!force && currentStatus !== 'idle') return;
    const known = new Set<string>(group.fields.map(([k]) => k));
    const targets: TargetFieldMapping[] = group.fields
      .map(([key, label]) => ({
        output_key: key,
        target_field: key,
        instruction: `Suggest ${label} using only the provided context; refuse if it is not supported.`,
        kind: 'text',
        max_drafts: 3,
        claim_policy: 'grounded',
        existing: existingFor(key),
        dismissed: [],
      }));
    if (targets.length === 0) {
      groupStatusRef.current[groupId] = 'loaded';
      setGroupFetches((s) => ({ ...s, [groupId]: { status: 'loaded', values: {}, refusals: {} } }));
      return;
    }
    const request: AISuggestRequest = {
      section: 'A',
      section_id: 'essentials',
      targets,
      tenant_context: tenantContext,
      section_context: sectionContext,
    };
    groupStatusRef.current[groupId] = 'loading';
    // Loading intentionally replaces the whole previous result. A refresh must
    // not leave a stale draft or stale official-source link visible.
    setGroupFetches((s) => ({ ...s, [groupId]: { status: 'loading' } }));
    Promise.resolve()
      .then(() => fetchSuggestions(request))
      .then(
        (resp) => {
          const values: Record<
            string,
            { value: string; source: string | null; sourceLinks: OfficialSourceLink[] }
          > = {};
          for (const d of resp.drafts) {
            // Drop an item whose target_field this button does not own — never
            // guess a field (the anti-transposition rule).
            if (!known.has(d.target_field)) continue;
            // Reject a contract-invalid draft rather than trust it: the backend
            // guarantees drafts arrive unverified/unaccepted. A rejected draft is
            // NOT recorded as a value — it falls through to the error state below.
            if (!isValidUnverifiedDraft(d)) continue;
            const trimmed = d.value.trim();
            if (values[d.target_field]) continue; // first valid draft per field
            const sourceLinks = officialSourceLinks(
              Array.isArray(d.source_urls) ? d.source_urls : [],
            );
            values[d.target_field] = {
              value: trimmed,
              // Keep server-internal evidence IDs out of operator-facing copy.
              source:
                sourceLinks.length > 0
                  ? 'supported by the official website'
                  : Array.isArray(d.source_context_keys) && d.source_context_keys.length
                    ? 'grounded in saved Brand Bible details'
                  : null,
              sourceLinks,
            };
          }
          const refusals: Record<string, string> = {};
          for (const r of resp.refusals) {
            if (!known.has(r.target_field)) continue; // unknown target dropped
            refusals[r.target_field] = r.reason;
          }
          groupStatusRef.current[groupId] = 'loaded';
          setGroupFetches((s) => ({ ...s, [groupId]: { status: 'loaded', values, refusals } }));
        },
        // Defined error semantics: a rejection is "could not ask" — surface it as
        // failure, never a silent degrade to no-suggestion.
        () => {
          groupStatusRef.current[groupId] = 'error';
          setGroupFetches((s) => ({ ...s, [groupId]: { status: 'error' } }));
        },
      );
  };

  // Resolve one field's display state from an EXPLICIT per-field status (never by
  // testing candidate truthiness — that is what let whitespace masquerade as a
  // value). Every field mirrors its button's fetch lifecycle.
  const fieldStateFor = (groupId: string, key: string): FieldSuggestionState => {
    const gs = groupFetches[groupId];
    if (gs && gs.status === 'loaded') {
      // Refusal (with its reason) wins.
      if (gs.refusals[key]) return { kind: 'refused', reason: gs.refusals[key] };
      // Only a VALID, non-whitespace draft (recorded in `values` at parse time)
      // is a value.
      const v = gs.values[key];
      if (v) return { kind: 'value', candidate: v.value, source: v.source, sourceLinks: v.sourceLinks };
      // We REQUESTED this field (every group field — description included —
      // is a target on the shared route), and central's service guarantees every target comes back in
      // drafts[] or refusals[]. So a requested field that is in NEITHER — or that
      // only produced a contract-invalid / whitespace draft — is a contract
      // violation on the wire, not "we asked and found nothing". Rendering it as
      // no-suggestion would be a lie of the exact kind our invariant forbids, so
      // surface it as an explicit error with Retry. (If central ever drops that
      // per-target guarantee, revisit this — an absent field would then be a
      // legitimate no-suggestion.)
      return { kind: 'error' };
    }
    if (gs && gs.status === 'error') return { kind: 'error' };
    return { kind: 'loading' };
  };

  // Per-field accept — BUFFERS EXACTLY ONE field via the same buffer setters the
  // inline inputs use, and NEVER touches verification state (no verifyV2 /
  // verifyIdentity / verified_* / approved_for_public). Accepting a suggestion is
  // an edit, so it stays unsaved until the section's Save, like any other edit.
  const acceptFor = (key: string, value: string) => {
    switch (key) {
      case 'business_name':
        setBuffer((b) => setDraftField(b, 'business_name', value, draft.business_name));
        break;
      case 'website':
        setBuffer((b) => setDraftField(b, 'website', value, draft.website));
        break;
      case 'phone':
        setBuffer((b) => setDraftField(b, 'phone', value, draft.phone));
        break;
      case 'email':
        setBuffer((b) => setDraftField(b, 'email', value, draft.email));
        break;
      case 'description':
        setBuffer((b) => setDraftField(b, 'description', value, draft.description));
        break;
      case 'service_locations':
        setBuffer((b) => setServiceLocations(b, parseDelimitedValues(value), draft.service_locations));
        break;
      case 'dba':
        setBuffer((b) => setIdentityField(b, 'dba', value, identity.entry?.dba ?? ''));
        break;
      case 'founded_year':
        setBuffer((b) => setIdentityField(b, 'founded_year', value, identity.entry?.founded_year?.toString() ?? ''));
        break;
      case 'team_size':
        setBuffer((b) => setIdentityField(b, 'team_size', value, identity.entry?.team_size?.toString() ?? ''));
        break;
      case 'booking_url':
        setBuffer((b) => setIdentityField(b, 'booking_url', value, identity.entry?.booking_url ?? ''));
        break;
      case 'socials': {
        const parsed = parseSocialsMap(value);
        if (parsed) {
          setBuffer((b) =>
            Object.entries(parsed).reduce((acc, [platform, url]) => setSocial(acc, platform, url, socialsMap), b),
          );
        }
        break;
      }
      case 'owner_name':
        setBuffer((b) => setOwnerField(b, 'name', value, identity.entry?.owner?.name ?? ''));
        break;
      case 'owner_title':
        setBuffer((b) => setOwnerField(b, 'title', value, identity.entry?.owner?.title ?? ''));
        break;
      case 'on_camera_ok': {
        const parsed = parseOnCameraOk(value);
        if (parsed !== null) {
          setBuffer((b) => setOwnerOnCamera(b, parsed, Boolean(identity.entry?.owner?.on_camera_ok)));
        }
        break;
      }
      case 'tracking_phone':
        setBuffer((b) => setLocationScalar(b, 'tracking_phone', value, primaryLocation?.tracking_phone ?? ''));
        break;
      case 'gbp_url':
        setBuffer((b) => setLocationScalar(b, 'gbp_url', value.trim(), primaryLocation?.gbp_url ?? ''));
        break;
      case 'service_radius_miles': {
        const n = Number(value);
        if (Number.isInteger(n)) {
          setBuffer((b) => setLocationRadius(b, n, primaryLocation?.service_radius_miles ?? null));
        }
        break;
      }
      case 'hours': {
        const parsed = parseHoursMap(value);
        if (parsed) {
          setBuffer((b) => setLocationHours(b, parsed, primaryLocation?.hours));
        }
        break;
      }
      default:
        break;
    }
  };

  const resolvedSuggestionGroups: ResolvedSuggestionGroup[] = SECTION_A_SUGGESTION_GROUPS.map((g) => ({
    id: g.id,
    label: g.label,
    status: groupFetches[g.id]?.status ?? 'idle',
    onOpen: () => fetchGroup(g.id),
    onRetry: () => fetchGroup(g.id, true),
    onRefresh: () => fetchGroup(g.id, true),
    fields: g.fields.map(([key, label]) => ({
      key,
      label,
      state: fieldStateFor(g.id, key),
      onAccept: (v: string) => acceptFor(key, v),
    })),
  }));
  // Look a group up BY ID for placement, never by array index — reordering
  // SECTION_A_SUGGESTION_GROUPS must not mislabel a placement wrapper.
  const suggestionGroupById = (id: string) => resolvedSuggestionGroups.find((g) => g.id === id);

  const pendingItems: PendingVerifyItem[] = [
    ...(identity.entry && !identity.entry.verified_by ? [{ table: 'identity' as const, id: identity.entry.id }] : []),
    ...(primaryLocation && !primaryLocation.verified_by
      ? [{ table: 'locations' as const, id: primaryLocation.id }]
      : []),
    ...licenseInsuranceCredentials
      .filter((c) => !c.verified_by)
      .map((c) => ({ table: 'credentials' as const, id: c.id })),
  ];

  // Sequential, honest-fail loop (Carl: never mask a partial failure as a
  // success) — each row keeps whatever verify state the server
  // actually landed it in; a retry only re-attempts rows still pending.
  const runVerifyAll = async () => {
    setVerifyAllState('running');
    let succeeded = 0;
    let failed = 0;
    for (const item of pendingItems) {
      try {
        await verifyV2(item.table, item.id);
        succeeded += 1;
      } catch {
        failed += 1;
      }
    }
    if (failed === 0) {
      setVerifyAllResult(null);
      setVerifyAllState('success');
    } else {
      setVerifyAllResult({ succeeded, failed });
      setVerifyAllState('partial');
    }
  };

  /* L9 #7: Phone + Email relocated into the primary location's details
     area (same draft.phone/draft.email store, labels unchanged). Defined
     once and re-used by both the details strip and the no-primary fallback
     so the two can never drift. */
  const contactReachFields = (
    <>
      <div data-testid="bb-essentials-loc-phone" className="min-w-[140px]">
        <FieldLabel required>Phone</FieldLabel>
        <TextField
          value={bufferedDraft(buffer, 'phone', draft.phone)}
          onChange={(v) => setBuffer((b) => setDraftField(b, 'phone', v, draft.phone))}
          placeholder="(555) 123-4567"
          readOnly={identityIsDraft}
          prefix={<Phone className="size-3.5" />}
        />
      </div>
      <div data-testid="bb-essentials-loc-email" className="min-w-[180px]">
        <FieldLabel>Email</FieldLabel>
        <TextField
          value={bufferedDraft(buffer, 'email', draft.email)}
          onChange={(v) => setBuffer((b) => setDraftField(b, 'email', v, draft.email))}
          prefix={<Mail className="size-3.5" />}
          type="email"
          readOnly={identityIsDraft}
        />
      </div>
    </>
  );

  function applyPrimaryDetailsPatch(patch: {
    service_radius_miles?: number | null;
    tracking_phone?: string;
    gbp_url?: string;
    hours?: Record<string, string>;
  }) {
    if (!primaryLocation) return;
    setBuffer((b) => {
      let next = b;
      if ('service_radius_miles' in patch) {
        next = setLocationRadius(
          next,
          patch.service_radius_miles ?? null,
          primaryLocation.service_radius_miles ?? null,
        );
      }
      if ('tracking_phone' in patch) {
        next = setLocationScalar(
          next,
          'tracking_phone',
          patch.tracking_phone ?? '',
          primaryLocation.tracking_phone ?? '',
        );
      }
      if ('gbp_url' in patch) {
        next = setLocationScalar(next, 'gbp_url', patch.gbp_url ?? '', primaryLocation.gbp_url ?? '');
      }
      if ('hours' in patch && patch.hours !== undefined) {
        next = setLocationHours(next, patch.hours, primaryLocation.hours);
      }
      return next;
    });
  }

  return (
    <section
      ref={(el) => {
        sectionRef(el);
      }}
      className="bg-card border border-border rounded-2xl px-6 py-5 mb-4 shadow-sm"
    >
      <BbSectionHeader
        {...BRAND_BIBLE_SECTION_BY_ID.essentials}
        right={
          <div data-testid="bb-essentials-verify-all" className="flex items-center gap-2.5">
            {canVerifyV2 && verifyAllState === 'success' && (
              <span className="text-[12px] font-medium text-[hsl(var(--status-success))]">✓ All verified</span>
            )}
            {canVerifyV2 && verifyAllState === 'partial' && verifyAllResult && (
              <button
                type="button"
                onClick={() => void runVerifyAll()}
                className="rounded-full border border-coral-dark bg-white px-3 py-1 text-[12px] font-semibold text-coral-dark hover:bg-coral-light/40"
              >
                {verifyAllResult.succeeded} verified · {verifyAllResult.failed} failed — retry
              </button>
            )}
            {canVerifyV2 && (verifyAllState === 'idle' || verifyAllState === 'running') && pendingItems.length > 0 && (
              <button
                type="button"
                disabled={verifyAllState === 'running'}
                onClick={() => void runVerifyAll()}
                className="rounded-full border border-coral-dark bg-white px-3 py-1 text-[12px] font-semibold text-coral-dark hover:bg-coral-light/40 disabled:opacity-60"
              >
                {verifyAllState === 'running' ? 'Verifying…' : `✓ Verify all · ${pendingItems.length} pending`}
              </button>
            )}
          </div>
        }
      />
      {identity.entry && (
        <div
          data-testid="bb-essentials-identity-verify"
          className="mb-3.5 flex items-center justify-between rounded-lg border border-border bg-warm-bg px-3.5 py-2"
        >
          <span className="text-[12px] font-semibold text-muted-foreground">Identity record</span>
          <div className="flex items-center gap-2.5">
            <VerificationRow
              verifiedBy={identity.entry.verified_by}
              verifiedAt={identity.entry.verified_at}
              canVerify={canVerifyV2}
              onVerify={verifyIdentity}
            />
          </div>
        </div>
      )}
      {draftRowsForTables(['identity', 'credentials']).length > 0 && (
        <div className="mb-4">
          <EnrichmentDraftsPanel
            tenantId={currentTenant ?? ''}
            drafts={draftRowsForTables(['identity', 'credentials'])}
            siteLabel={tenantInfo?.website || currentTenant || 'the client site'}
            canVerify={canVerifyV2}
            onEdit={goToDraftSection}
            onConfirmed={onDraftConfirmed}
            sectionLabelFor={sectionLabelFor}
          />
        </div>
      )}
      {suggestUiEnabled && (
        <div className="mb-2 flex justify-end" data-testid="bb-suggest-placement-business-identity">
          <FieldSuggestionGroup group={suggestionGroupById('business-identity')!} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3.5">
        <div className="col-span-2">
          <div className="flex items-center gap-2">
            <FieldLabel required hint='shown as "Business name"'>Legal name</FieldLabel>
            {businessNameDup && canVerifyV2 && (
              <button
                type="button"
                onClick={() => {
                  void verifyV2(businessNameDup.table, businessNameDup.entry.id).catch(
                    (e: unknown) =>
                      message.error(
                        `Verify failed — ${e instanceof Error ? e.message : 'request failed'}`,
                      ),
                  );
                }}
                className="mb-1.5 inline-flex items-center gap-1 rounded-full bg-coral-light px-2.5 py-0.5 text-[11px] font-semibold text-coral-dark hover:bg-coral-light/70"
              >
                found on site · Verify
              </button>
            )}
          </div>
          {identityIsDraft && (
            <p data-testid="bb-essentials-identity-readonly" className="mb-1.5 text-[11.5px] text-muted-foreground">
              Confirm identity to edit. These values were found on the web and are read-only until you confirm them.
            </p>
          )}
          <TextField
            value={bufferedDraft(buffer, 'business_name', draft.business_name)}
            onChange={(v) => setBuffer((b) => setDraftField(b, 'business_name', v, draft.business_name))}
            placeholder="e.g. Acme Plumbing & Heating"
            readOnly={identityIsDraft}
          />
        </div>

        <div>
          {/* rest-of-§A #17: no inline Verify pill here — it verified the WHOLE
              bb_identity record, not DBA. Whole-record verify has one honest
              home (the Identity record row) plus Verify-all. */}
          <FieldLabel hint="what customers call them">DBA</FieldLabel>
          <IdentityField
            key={`dba-${identity.entry?.id ?? 'pending'}-${discardNonce}`}
            value={identity.entry?.dba ?? ''}
            onCommit={(v) => setBuffer((b) => setIdentityField(b, 'dba', v, identity.entry?.dba ?? ''))}
          />
        </div>

        <div>
          {/* rest-of-§A #17: same pill removed — Founded is not a whole-record
              verify write. */}
          <FieldLabel>Founded</FieldLabel>
          <IdentityField
            key={`founded-${identity.entry?.id ?? 'pending'}-${discardNonce}`}
            value={identity.entry?.founded_year?.toString() ?? ''}
            onCommit={(v) =>
              setBuffer((b) => setIdentityField(b, 'founded_year', v, identity.entry?.founded_year?.toString() ?? ''))
            }
            placeholder="e.g. 1998"
          />
        </div>

        <div>
          <FieldLabel>Team size</FieldLabel>
          <IdentityField
            key={`team-size-${identity.entry?.id ?? 'pending'}-${discardNonce}`}
            value={identity.entry?.team_size?.toString() ?? ''}
            onCommit={(v) =>
              setBuffer((b) => setIdentityField(b, 'team_size', v, identity.entry?.team_size?.toString() ?? ''))
            }
            placeholder="e.g. 12"
          />
        </div>

        <div>
          <FieldLabel required hint="Used for SEO tracking">
            Primary website
          </FieldLabel>
          <TextField
            value={bufferedDraft(buffer, 'website', draft.website)}
            onChange={(v) => setBuffer((b) => setDraftField(b, 'website', v, draft.website))}
            placeholder="https://example.com"
            readOnly={identityIsDraft}
            prefix={<Globe className="size-3.5" />}
          />
          {/* rest-of-§A #18: controlled per-keystroke input — typing stays
              free, but a non-empty invalid value is flagged, never silent. */}
          {(() => {
            const value = bufferedDraft(buffer, 'website', draft.website).trim();
            if (!value || isValidHttpUrl(value)) return null;
            return (
              <p data-testid="bb-essentials-website-error" className="mt-1 text-[11px] text-destructive">
                {URL_HINT}
              </p>
            );
          })()}
        </div>
      </div>

      <div className="mt-3.5 rounded-lg border border-border bg-warm-bg">
        <div className="px-3.5 py-2.5 border-b border-border/60">
          <span className="text-[13px] font-semibold">Owner</span>
        </div>
        <div className="grid grid-cols-[1.2fr_1fr_auto] items-end gap-3 p-3.5">
          <div>
            <FieldLabel>Name</FieldLabel>
            <IdentityField
              key={`owner-name-${identity.entry?.id ?? 'pending'}-${discardNonce}`}
              value={identity.entry?.owner?.name ?? ''}
              onCommit={(v) => setBuffer((b) => setOwnerField(b, 'name', v, identity.entry?.owner?.name ?? ''))}
            />
          </div>
          <div>
            <FieldLabel>Title</FieldLabel>
            <IdentityField
              key={`owner-title-${identity.entry?.id ?? 'pending'}-${discardNonce}`}
              value={identity.entry?.owner?.title ?? ''}
              onCommit={(v) => setBuffer((b) => setOwnerField(b, 'title', v, identity.entry?.owner?.title ?? ''))}
            />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
              On camera
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={bufferedOnCamera(buffer, identity.entry?.owner?.on_camera_ok)}
              aria-label="Owner on-camera OK"
              onClick={() =>
                setBuffer((b) =>
                  setOwnerOnCamera(
                    b,
                    !bufferedOnCamera(b, identity.entry?.owner?.on_camera_ok),
                    Boolean(identity.entry?.owner?.on_camera_ok),
                  ),
                )
              }
              className={cn(
                'relative h-5 w-9 flex-shrink-0 rounded-full transition-colors',
                bufferedOnCamera(buffer, identity.entry?.owner?.on_camera_ok) ? 'bg-coral' : 'bg-teal-light',
              )}
            >
              <span
                className={cn(
                  // left-0 pins the thumb's origin — see VoiceProfilePanel:
                  // without it `left:auto` resolves to the ~18px static
                  // position and stacks with the translate (18+18=36px),
                  // pushing the thumb past the track's right edge.
                  'absolute left-0 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform',
                  bufferedOnCamera(buffer, identity.entry?.owner?.on_camera_ok)
                    ? 'translate-x-[18px]'
                    : 'translate-x-0.5',
                )}
              />
            </button>
          </div>
        </div>
      </div>

      {licenseInsuranceCredentials.length > 0 && (
        <div className="mt-3.5 rounded-lg border border-border bg-warm-bg">
          <div className="px-3.5 py-2.5 border-b border-border/60">
            <span className="text-[13px] font-semibold">Licenses & insurance</span>
          </div>
          <div className="divide-y divide-border/60">
            {licenseInsuranceCredentials.map((c) => (
              <div
                key={c.id}
                data-testid={`bb-essentials-credential-verify-${c.id}`}
                className="flex items-center justify-between gap-3 px-3.5 py-2.5"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-[13px] font-medium">
                    <span className="capitalize text-muted-foreground">{c.kind}</span>
                    {(c.name || c.number) && ` · ${[c.name, c.number].filter(Boolean).join(' · ')}`}
                  </div>
                  {c.expiry && (
                    <div className="text-[11px] text-muted-foreground">
                      Expires {new Date(c.expiry).toLocaleDateString()}
                    </div>
                  )}
                </div>
                <div className="flex flex-shrink-0 items-center gap-2">
                  <VerificationRow
                    verifiedBy={c.verified_by}
                    verifiedAt={c.verified_at}
                    canVerify={canVerifyV2}
                    onVerify={() =>
                      void verifyV2('credentials', c.id).catch((e: unknown) =>
                        message.error(`Verify failed — ${e instanceof Error ? e.message : 'request failed'}`),
                      )
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {suggestUiEnabled && (
        <div className="mt-3.5 flex justify-end" data-testid="bb-suggest-placement-contact-reach">
          <FieldSuggestionGroup group={suggestionGroupById('contact-reach')!} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3.5 mt-3.5">
        {/* L9 #7: Phone + Email moved INTO the primary location's details
            area (FE-only — same draft.phone/draft.email store). When no
            primary row exists they render standalone below the list so the
            fields never disappear. */}
        <div>
          {/* rest-of-§A #17: third whole-record pill removed. */}
          <FieldLabel hint="estimate / scheduling link">Booking URL</FieldLabel>
          <IdentityField
            key={`booking-url-${identity.entry?.id ?? 'pending'}-${discardNonce}`}
            value={identity.entry?.booking_url ?? ''}
            onCommit={(v) => {
              // rest-of-§A #18: reject a non-empty invalid URL — it never
              // reaches the buffer.
              const trimmed = v.trim();
              if (trimmed && !isValidHttpUrl(trimmed)) {
                setBookingUrlError(true);
                return;
              }
              setBookingUrlError(false);
              setBuffer((b) => setIdentityField(b, 'booking_url', trimmed, identity.entry?.booking_url ?? ''));
            }}
            placeholder="https://example.com/free-estimate"
            prefix={<Globe className="size-3.5" />}
          />
          {bookingUrlError && (
            <p data-testid="bb-essentials-booking-url-error" className="mt-1 text-[11px] text-destructive">
              {URL_HINT}
            </p>
          )}
        </div>
        <div>
          <FieldLabel>Socials</FieldLabel>
          <div className="flex flex-wrap items-center gap-2">
            {Object.entries(effectiveSocials).map(([platform, url]) => (
              <span
                key={platform}
                title={url}
                className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium"
              >
                {platform}
                <button
                  type="button"
                  aria-label={`Remove ${platform}`}
                  onClick={() => setBuffer((b) => setSocial(b, platform, null, socialsMap))}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
            <input
              value={socialDraft}
              onChange={(e) => {
                setSocialDraft(e.target.value);
                setSocialError(null);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  bufferSocialFromDraft();
                }
              }}
              placeholder="+ add — e.g. Facebook: https://…"
              aria-invalid={socialError ? true : undefined}
              className={[
                'min-w-[200px] flex-1 rounded-full border border-dashed px-3 py-1 text-xs text-teal outline-none focus:border-coral',
                socialError ? 'border-destructive' : 'border-input bg-warm-bg',
              ].join(' ')}
            />
            {socialError && (
              <p data-testid="bb-essentials-social-error" className="mt-1 text-[11px] text-destructive">
                {socialError}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3.5">
        <LocationList
          primaryContact={{
            phone: bufferedDraft(buffer, 'phone', draft.phone),
            email: bufferedDraft(buffer, 'email', draft.email),
          }}
          onPrimaryDetailsPatch={applyPrimaryDetailsPatch}
          primaryDetails={
            primaryLocation ? (
              <div
                data-testid="bb-essentials-location-fields"
                className="border-t border-border bg-coral-light pt-1.5 pb-3 pl-[50px] pr-5 text-[13px] shadow-[inset_3px_0_0_var(--color-coral)]"
              >
                <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
                  {/* L9 #7: Phone + Email live in the primary details area.
                      Radius/Tracking/GBP/Hours moved onto every row's Edit
                      panel (ruling 2026-08-18). Tracking never autofills these. */}
                  {contactReachFields}
                </div>
              </div>
            ) : (
              /* L9 #7 fallback: no primary row yet — the contact fields still
                 render (Phone is required), just outside a details strip. */
              <div
                data-testid="bb-essentials-location-fields"
                className="flex flex-wrap items-end gap-x-4 gap-y-2 border-t border-border px-4 pt-2 pb-3 text-[13px]"
              >
                {contactReachFields}
              </div>
            )
          }
        />
        {/* L9 #9: the service-areas chips row is unmounted — one geographic
            input per concept. Address rows (LocationList) + the labeled
            AlsoServingBand inside it are the coverage surface. Primary details
            ride under the starred row; writePart / ADDRESS_PARTS /
            ConflictRow stay exported for #1670. */}
      </div>

      {suggestUiEnabled && (
        <div className="mt-3.5 flex justify-end" data-testid="bb-suggest-placement-description-location">
          <FieldSuggestionGroup group={suggestionGroupById('description-location')!} />
        </div>
      )}
      <div className="grid grid-cols-2 gap-3.5 mt-3.5">
        <div className="col-span-2">
          <FieldLabel hint="notes — not a typed spec field; convenience for the team">
            Brand overview
          </FieldLabel>
          <TextArea
            value={bufferedDraft(buffer, 'description', draft.description)}
            onChange={(v) => setBuffer((b) => setDraftField(b, 'description', v, draft.description))}
            rows={2}
            placeholder="One paragraph: who you are, what you do, where you do it."
            readOnly={identityIsDraft}
            className="bg-teal-light/40 text-muted-foreground"
          />
        </div>
      </div>

      {/* T2 read-mostly enrichment strip (source-owned; renders whenever the
          backend projects enrichment for this location — pending the sidecar
          PR). Display-only, with a Refresh stub; never editable. */}
      {locationEnrichment && (
        <EnrichmentStrip enrichment={locationEnrichment} onRefresh={() => refreshLocationEnrichment?.()} />
      )}
    </section>
  );
}
